/**
 * 数据埋点
 */

import dayjs from 'dayjs';
import { BuriedPointParams, BuriedPointInfo, EventInfoVo } from './types/buried-point';
import http from './http-request';
import { v4 as uuidv4 } from 'uuid';
import browser from '@/core/browser';
import { isInWhiteListHost } from './utils';

const storageKey = 'ovo_buriedPoint';
const timeFormat = 'YYYY-MM-DD HH:mm:ss';

/** 初始化时的延迟上报埋点数据定时器 */
let timeoutId: NodeJS.Timeout | null = null;

/** 是否禁用埋点上报 */
const disabled = !isInWhiteListHost();
/**
 * 初始化埋点
 */
export const initBuriedPoint = () => {
  if (disabled) {
    return;
  }

  // 页面卸载时，更新本地缓存的页面离开时间
  window.addEventListener('beforeunload', updateLastPageLeaveTime);

  // 页面加载后延迟上报缓存的埋点数据，防止阻塞页面加载
  timeoutId = setTimeout(buriedPointUpload, 3000);

  // 待 window.ovopark 初始化完成
  setTimeout(setCommonInfo, 0);
};

/**
 * 数据埋点上报
 * @param params 埋点参数
 */
export const buriedPointUpload = (info?: BuriedPointInfo) => {
  if (disabled) {
    return;
  }

  if (!info || (!info.eventCode && !info.pageCode)) {
    // 没有参数，或者参数中没有事件编码和页面编码，上报本地缓存的埋点数据
    const eventInfos = updateLastPageLeaveTime();
    if (eventInfos) {
      uploadInfo(eventInfos);
    }
    return;
  }

  const eventInfo: EventInfoVo = {
    entryTime: dayjs().format(timeFormat),
    currentUrl: window.location.href,
    referUrl: window.document.referrer,
    ...info,
    _eventId: uuidv4(),
  };

  // 事件埋点
  if (info.eventCode) {
    // 兼容处理，防止点击事件后跨项目跳转了，先缓存到本地，上报后再删除
    addEventInfo(eventInfo);
    uploadInfo([eventInfo]);
    return;
  }

  // 页面埋点，上报之前缓存的数据，缓存新埋点
  const eventInfos = updateLastPageLeaveTime();
  addEventInfo(eventInfo);
  if (eventInfos) {
    if (timeoutId) {
      // 清除初始化时的延迟上报缓存数据
      clearTimeout(timeoutId);
      timeoutId = null;
    }
    uploadInfo(eventInfos);
  }
};

/**
 * 获取本地缓存的埋点信息
 */
const getStorageEventInfos = () => {
  const eventInfos = localStorage.getItem(storageKey);
  if (eventInfos) {
    return JSON.parse(eventInfos) as EventInfoVo[];
  }
};
/**
 * 更新本地缓存的页面离开时间
 */
const updateLastPageLeaveTime = () => {
  const eventInfos = getStorageEventInfos();
  if (eventInfos?.length) {
    const pageEventInfo = eventInfos.findLast(
      (item) => item.pageCode && !item.eventCode && !item.leaveTime,
    );
    if (pageEventInfo) {
      const entryTime = dayjs(pageEventInfo.entryTime);
      if (entryTime.isBefore(dayjs().subtract(2, 'hour'))) {
        // 如果页面进入时间超过2小时，作为异常数据，更新离开时间为1分钟
        pageEventInfo.leaveTime = entryTime.add(1, 'minute').format(timeFormat);
      } else {
        pageEventInfo.leaveTime = dayjs().format(timeFormat);
      }
      // 更新本地缓存的埋点信息
      localStorage.setItem(storageKey, JSON.stringify(eventInfos));
    }
  }
  return eventInfos;
};
/**
 * 添加埋点信息
 * @param eventInfo 埋点事件信息
 */
const addEventInfo = (eventInfo: EventInfoVo) => {
  const eventInfos = getStorageEventInfos() || [];
  eventInfos.push(eventInfo);
  localStorage.setItem(storageKey, JSON.stringify(eventInfos));
};

/**
 * 上报埋点数据
 * @param eventInfos 埋点信息
 */
const uploadInfo = async (eventInfos: EventInfoVo[]) => {
  if (!eventInfos || eventInfos.length === 0) {
    return;
  }

  if (!commonInfo.requestId) {
    // 没有请求ID，则调用api获取
    const myInfo = await window.ovopark.getMyInfo();
    commonInfo.userId = myInfo.data.id + '';
    commonInfo.enterpriseId = myInfo.data.groupId;
    commonInfo.requestId = `${
      window.ovopark.tokenMgmt.refreshExpirationTimestamp || dayjs().format('YYYYMMDD')
    }-${myInfo.data.id}`;
  }

  await http({
    method: 'POST',
    url: '/buriedPoints/acquisition/v1/upload/uploadBp',
    json: {
      ...commonInfo,
      eventInfoList: eventInfos,
    },
  });

  // 删除已上报的埋点信息
  const newEventInfos = getStorageEventInfos() || [];
  eventInfos.forEach((item) => {
    const idx = newEventInfos.findIndex((event) => event._eventId === item._eventId);
    if (idx > -1) {
      newEventInfos.splice(idx, 1);
    }
  });
  localStorage.setItem(storageKey, JSON.stringify(newEventInfos));
};

const commonInfo: Partial<BuriedPointParams> = {};
/**
 * 读取埋点设备信息
 */
const setCommonInfo = () => {
  if (browser.ovopark && window.ovopark.getAppFunction('getBuriedPointInfo')) {
    // 调万店掌App协议获取
    window.ovopark.action('getBuriedPointInfo').then((res: any) => {
      Object.assign(commonInfo, res);
    });
    return;
  }

  const deviceInfo: Record<string, any> = {
    // model: 'unknown',
    // deviceId: 'unknown',
    networkType: 'wifi',
    screenWidth: window.innerWidth,
    screenHeight: window.innerHeight,
  };
  commonInfo.deviceInfo = deviceInfo;
  // commonInfo.appInfo = {
  //   appVersion: 'h5',
  // };

  // 移动端提供操作系统信息
  if (browser.android) {
    deviceInfo.os = 'android';
    deviceInfo.osVersion = browser.androidVersion;
    return;
  }
  if (browser.ios) {
    deviceInfo.os = 'ios';
    deviceInfo.osVersion = browser.iosVersion;
    return;
  }
  if (browser.harmony) {
    deviceInfo.os = 'harmony';
    deviceInfo.osVersion = browser.harmonyVersion;
    return;
  }
  // web端提供浏览器信息
  deviceInfo.osVersion = browser.version;
  if (browser.edge) {
    deviceInfo.os = 'edge';
    return;
  }
  if (browser.firefox) {
    deviceInfo.os = 'firefox';
    return;
  }
  if (browser.chrome) {
    deviceInfo.os = 'chrome';
    return;
  }
  if (browser.opera) {
    deviceInfo.os = 'opera';
    return;
  }
  if (browser.safari) {
    deviceInfo.os = 'safari';
    return;
  }
  if (browser.ie) {
    deviceInfo.os = 'ie';
    return;
  }
  if (browser.x5) {
    deviceInfo.os = 'x5';
    return;
  }
  deviceInfo.os = 'other';
};
